import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { FlowStatus } from '@/api/generated';
import {
  EXTERNAL_SSO_CODE_PARAM,
  postExternalSsoCodeToOpener,
} from '@/components/factors/external-sso-factor/external-sso-popup';
import { StatusContainer } from '@/components/ui/status-container';
import { useLanguage } from '@/contexts/language-context';
import { StatusVariant } from '@/enums/status-variant';
import type { MfaFlowApiResponse } from '@/hooks/use-mfa-orchestrator.types';
import { useSignOn } from '@/hooks/use-sign-on';
import { makeFullPath } from '@/utils/path-utils';
import { navigateToSplitSigning, submitSsoCompletion } from '@/utils/sso-utils';
import { ValidatingPage } from './validating-page';

/**
 * Welcome screen component with sign-in functionality
 */
export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { endSignOn } = useSignOn();
  const { t } = useLanguage();
  const authId = searchParams.get('auth_id') ?? '';
  const isEscalation = searchParams.get('escalation') === 'true';
  const isEnrollment = searchParams.get('enrollment') === 'true';
  const hasSsoCallback = searchParams.has(EXTERNAL_SSO_CODE_PARAM);
  const externalSsoCode = searchParams.get(EXTERNAL_SSO_CODE_PARAM) ?? '';

  useEffect(() => {
    if (externalSsoCode) {
      postExternalSsoCodeToOpener(externalSsoCode);
    }
  }, [externalSsoCode]);

  // username is undefined on escalation/cookie-resume paths (no LoginUI input).
  const onSuccessLogin = async (
    result: MfaFlowApiResponse,
    username?: string
  ) => {
    // Check if authentication is already complete
    if (result.status === FlowStatus.COMPLETED) {
      // Completed → redirecting; no card follows, so drop the cover.
      endSignOn();

      // For enrollment flows, completion is handled via postMessage in mfa-factor.tsx
      // Don't call SAML redirect for enrollment flows
      if (isEnrollment) {
        console.info('Enrollment flow completed - handled via postMessage');

        return;
      }

      try {
        const completion = await submitSsoCompletion();

        if (completion) {
          navigateToSplitSigning(navigate, completion, username);
        }
      } catch (error) {
        console.error('Failed to load SAML response:', error);

        captureException(error, {
          tags: { flow_status: result.status },
        });
      }

      return;
    }

    if (result.status === FlowStatus.FAILED) {
      captureException(new Error('Authentication failed'), {
        tags: {
          flow_status: result.status,
          factor_type: result.currentStep?.factorType,
          challenge_status: result.currentStep?.status,
        },
      });
    }

    const { currentStep, hasAlternative } = result;
    const factorType = currentStep?.factorType;

    // Route to the universal factor screen - FactorRouter will handle the specific factor type
    // Flow and step IDs are now managed by session cookies
    if (factorType) {
      // Preserve escalation or enrollment parameter if present
      const escalationParam = isEscalation ? '&escalation=true' : '';
      const enrollmentParam = isEnrollment ? '&enrollment=true' : '';
      navigate(
        `${makeFullPath('/mfa-factor')}?factorType=${encodeURIComponent(factorType)}&authId=${encodeURIComponent(authId)}${escalationParam}${enrollmentParam}`,
        {
          state: {
            currentStepData: currentStep,
            hasAlternative: hasAlternative ?? false,
            username,
          },
        }
      );
    }
  };

  if (hasSsoCallback) {
    if (externalSsoCode && window.opener) {
      return null;
    }

    return (
      <div className='min-h-screen flex-1 relative content-center'>
        <StatusContainer
          variant={StatusVariant.ERROR}
          title={t('mfa.error')}
          message={t('externalSso.returnToOriginalWindow')}
        />
      </div>
    );
  }

  return (
    <div className='min-h-screen flex-1 relative content-center'>
      <div className='flex-1'>
        <ValidatingPage
          onSuccessLogin={onSuccessLogin}
          authId={authId}
          isEscalation={isEscalation}
          isEnrollment={isEnrollment}
        />
      </div>
    </div>
  );
}
